import { useEffect, useRef, useState } from "react";
import { PREV_YEF_PHOTOS } from "@/lib/eventData";
import SectionHeader from "@/components/gala/SectionHeader";
import { ChevronLeft, ChevronRight, X, Expand } from "lucide-react";

const AUTOPLAY_MS = 5000;

export default function PhotoGallery() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const timerRef = useRef(null);

  const total = PREV_YEF_PHOTOS.length;
  const current = PREV_YEF_PHOTOS[index];

  const go = (dir) =>
    setIndex((i) => (i + dir + total) % total);

  useEffect(() => {
    if (paused || lightbox || total <= 1) return;
    timerRef.current = window.setTimeout(() => go(1), AUTOPLAY_MS);
    return () => window.clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, paused, lightbox, total]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="section relative"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Moments of Havana"
          title="A Glimpse of the Previous YEF Celebrations"
          description="A reel of memories from our past year-end functions — the laughs, the dancefloor and the quiet in-between moments that make it all worth it."
        />

        <div
          data-testid="gallery-slideshow"
          className="relative rounded-sm overflow-hidden border border-[#D4AF37]/35 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] bg-[#0B2820]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Slide stack with crossfade */}
          <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] bg-[#0B2820]">
            {PREV_YEF_PHOTOS.map((p, i) => (
              <img
                key={p.src}
                src={p.src}
                alt={p.caption || `Previous YEF ${i + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-out ${
                  i === index ? "opacity-100" : "opacity-0"
                }`}
                loading={i === 0 ? "eager" : "lazy"}
              />
            ))}

            {/* Soft bottom gradient (subtle, no caption) */}
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

            {/* Counter */}
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-black/55 backdrop-blur px-2.5 sm:px-3 py-1 rounded-full text-[10px] tracking-[0.3em] font-display text-[#F3E08A] border border-[#D4AF37]/40">
              <span data-testid="gallery-index">{index + 1}</span>
              <span className="opacity-50"> / {total}</span>
            </div>

            {/* Expand to lightbox */}
            <button
              data-testid="gallery-expand"
              onClick={() => setLightbox(true)}
              aria-label="View larger"
              className="absolute top-3 left-3 sm:top-4 sm:left-4 w-9 h-9 grid place-items-center rounded-full bg-black/55 backdrop-blur border border-[#D4AF37]/40 text-[#F3E08A] hover:border-[#D4AF37] transition"
            >
              <Expand size={15} />
            </button>

            {/* Prev / Next */}
            {total > 1 && (
              <>
                <button
                  data-testid="gallery-prev"
                  onClick={() => go(-1)}
                  aria-label="Previous photo"
                  className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 grid place-items-center rounded-full bg-black/55 hover:bg-black/80 backdrop-blur border border-[#D4AF37]/40 text-[#F3E08A] transition"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  data-testid="gallery-next"
                  onClick={() => go(1)}
                  aria-label="Next photo"
                  className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 grid place-items-center rounded-full bg-black/55 hover:bg-black/80 backdrop-blur border border-[#D4AF37]/40 text-[#F3E08A] transition"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
          </div>

          {/* Dot indicators */}
          {total > 1 && (
            <div
              data-testid="gallery-dots"
              className="flex items-center justify-center gap-2 py-4 bg-[#0B2820]"
            >
              {PREV_YEF_PHOTOS.map((_, i) => (
                <button
                  key={i}
                  data-testid={`gallery-dot-${i}`}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to photo ${i + 1}`}
                  className={`h-[6px] rounded-full transition-all duration-300 ${
                    i === index
                      ? "w-8 bg-[#D4AF37]"
                      : "w-2 bg-[#D4AF37]/35 hover:bg-[#D4AF37]/70"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[80] bg-[#0B2820]/95 backdrop-blur-md grid place-items-center p-4"
          onClick={() => setLightbox(false)}
        >
          <button
            data-testid="gallery-lightbox-close"
            onClick={() => setLightbox(false)}
            aria-label="Close"
            className="absolute top-6 right-6 w-11 h-11 grid place-items-center border border-[#D4AF37]/50 text-[#F3E08A] hover:border-[#D4AF37] transition"
          >
            <X size={18} />
          </button>
          <figure
            className="max-w-6xl w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={current.src}
              alt={current.caption || ""}
              className="w-full max-h-[82vh] object-contain border border-[#D4AF37]/40"
            />
            {current.caption && (
              <figcaption className="sr-only">
                {current.caption}
              </figcaption>
            )}

            {total > 1 && (
              <>
                <button
                  data-testid="gallery-lightbox-prev"
                  onClick={() => go(-1)}
                  aria-label="Previous photo"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center rounded-full bg-black/55 border border-[#D4AF37]/40 text-[#F3E08A] hover:border-[#D4AF37]"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  data-testid="gallery-lightbox-next"
                  onClick={() => go(1)}
                  aria-label="Next photo"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center rounded-full bg-black/55 border border-[#D4AF37]/40 text-[#F3E08A] hover:border-[#D4AF37]"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
          </figure>
        </div>
      )}
    </section>
  );
}
